import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  CollapseButton,
  CollapseSection,
  EditableTypography,
  isImageOr3dModel,
  isPdfOrFile,
  ModalOpenButton,
  processAttachment,
  TextWithOverflow,
  useScreenWidth,
} from "@msys/ui";
import { Check as CheckIcon, Edit as EditIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  List,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import { useTranslate } from "@tolgee/react";
import { noop, partition } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import { useLatest } from "react-use";
import { color } from "../../../common/MuiThemeProvider.js";
import { RestrictedByDocumentPermissionWithDebug } from "../../auth/RestrictedByDocumentPermission.js";
import { useUserData } from "../../auth/useUserData.js";
import { FileListItemButton } from "../../commons/files/FileListItemButton.js";
import { GalleryRow } from "../../commons/images/GalleryRow.js";
import { LabeledSignatureValue } from "../../commons/labeled-values/LabeledSignatureValue.js";
import { NoPropagateBox } from "../../commons/NoPropagateBox.js";
import { TIME_AND_MATERIAL_CONTRACT_TYPES } from "../../constants.js";
import {
  ALL_KEY,
  ExpandedStore,
} from "../../trees-virtual/hooks/useExpandedStore.js";
import { FilesBoxTable } from "../attachments/FilesBoxTable.js";
import { useModifyItemCalculation } from "../doc-data-grid/useModifyItemCalculation.js";
import { useModifyItemInfo } from "../doc-data-grid/useModifyItemInfo.js";
import { useModifyItemProduct } from "../doc-data-grid/useModifyItemProduct.js";
import { ContingencyCheckbox } from "../doc-items/fields/ContingencyCheckbox.js";
import { DecisionOptionRadioOrCheckbox } from "../doc-items/fields/DecisionOptionRadioOrCheckbox.js";
import { SubitemsOptionsNoneSection } from "../doc-items/fields/SubitemsOptionsNoneSection.js";
import { getViewerDecisionRole } from "../doc-items/helpers.js";
import { useDecisionItemMutations } from "../doc-items/hooks/useDecisionItemMutations.js";
import { DocumentActorData } from "../documents/DocumentActorData.js";
import { DocumentPreviewItems } from "../documents/DocumentPreviewItems.js";
import {
  AdditionalColumn,
  Fields,
} from "../documents/DocumentPreviewItemsTypes.js";
import { useTreeItemIdsWithWizardQuestions } from "../documents/useWizardContext.js";
import { RefinementWizardButton } from "../projects/quote-trees.js";
import { S4HanaQuoteDataBox } from "../sap-s4-hana/S4HanaQuoteDataBox.js";
import { filterItemProductImages } from "./helpers.js";
import { QuoteDocumentActorsModal } from "./modals/QuoteDocumentActorsModal.js";
import { QuotePreviewDates } from "./preview/QuotePreviewDates.js";
import { QuotePreviewHeader } from "./preview/QuotePreviewHeader.js";
import {
  quoteItemHasDescription,
  QuotePreviewItemDescription,
} from "./preview/QuotePreviewItemDescription.js";
import { QuotePreviewSummary } from "./preview/QuotePreviewSummary.js";
import { EditableQuantityCell } from "./preview/EditableQuantityCell.js";
import {
  PendingChangeValue,
  PreviewValue,
} from "./preview/QuotePreviewValues.js";
import { ClosingTextInput } from "./QuoteClosingText.js";
import { OpeningTextInput } from "./QuoteOpeningText.js";
import {
  QuotePreview_ItemFragment,
  QuotePreview_QuoteFragment,
  useModifyQuoteTexts_QuotePreviewMutation,
  useQuoteItemSapS4HanaData_QuotePreviewQuery,
} from "./QuotePreview.generated.js";

interface Props<CanFinalize extends boolean> {
  navigateToItem?: (item: QuotePreview_ItemFragment) => void;
  selectedItemId?: string | null;
  projectId: string;
  quote: QuotePreview_QuoteFragment;
  container?: HTMLElement | null;
  expandedStore?: ExpandedStore;
  expandedItemIds: string[] | undefined;
  hasSapS4HanaIntegration: boolean;
  refetch?: () => Promise<unknown>;
  refetchAfterApplyItemAction?: () => Promise<unknown>;
  canEdit: boolean;
}

export const QuotePreview = <CanFinalize extends boolean>({
  navigateToItem,
  selectedItemId,
  projectId,
  quote,
  container,
  expandedStore,
  expandedItemIds,
  hasSapS4HanaIntegration,
  refetch,
  refetchAfterApplyItemAction,
  canEdit,
}: Props<CanFinalize>) => {
  const { enqueueSnackbar } = useSnackbar();
  const { isMinTablet, isMinDesktop } = useScreenWidth();
  const viewer = useUserData().currentUser!;

  const { t } = useTranslate([
    "Quote",
    "QuoteStateBadge",
    "Global",
    "Decisions",
  ]);

  const client = useApolloClient();
  const [modifyQuoteTexts] = useModifyQuoteTexts_QuotePreviewMutation({
    client,
  });
  const querySapS4HanaData = useQuoteItemSapS4HanaData_QuotePreviewQuery({
    variables: { projectId: quote.projectId, docId: quote.id },
    skip: !hasSapS4HanaIntegration,
  });
  const sapS4HanaData = React.useMemo(
    () =>
      getDataOrNull(querySapS4HanaData.data?.quoteItemSapS4HanaData)?.items ??
      [],
    [querySapS4HanaData.data?.quoteItemSapS4HanaData]
  );
  const hasS4HanaData = hasSapS4HanaIntegration && sapS4HanaData.length > 0;

  const doc = quote;

  const isViewersDocument =
    doc.owningSystemOrganisationId === viewer.organisation.id;

  const isAgreed =
    (quote?.isPublished && quote?.isBinding && quote?.agreement === "YES") ??
    false;

  const editable = canEdit && isViewersDocument && !isAgreed;

  const { modifyItemInfo } = useModifyItemInfo({
    projectId,
    docId: quote.id,
  });
  const { modifyItemProduct } = useModifyItemProduct({
    projectId,
    docId: quote.id,
  });
  const { modifyItemCalculation } = useModifyItemCalculation({
    projectId,
    docId: quote.id,
  });

  const viewerDecisionRole = getViewerDecisionRole(doc.actors);

  const allDocItems = doc.items;

  const rootItem = allDocItems.find(item => item.isRootItem);

  let [images, files] = partition(
    (rootItem?.attachments ?? [])
      .filter(attachment => attachment.clientVisibility)
      .map(processAttachment),
    isImageOr3dModel
  );
  images = filterItemProductImages(images, quote.productsVisibility);

  const isTimeAndMaterialContract = TIME_AND_MATERIAL_CONTRACT_TYPES.includes(
    quote.contractType
  );

  const saveQuoteText = async (content: any) => {
    try {
      await modifyQuoteTexts({
        variables: {
          input: {
            projectId: quote.projectId,
            docId: quote.id,
            values: {
              quoteTexts: content,
            },
          },
        },
      });
    } catch (error) {
      if (error instanceof Error)
        enqueueSnackbar(error.message, { variant: "error" });
    }
  };

  const [hasItemAnyQuestions, refetchWizardQuestions] =
    useTreeItemIdsWithWizardQuestions({
      sourceDoc: {
        type: "quote",
        quoteId: doc.id,
        contractorId: viewer.organisation.id,
      },
      wizardContext: "onQuoteRefinement",
    });

  const {
    handleDecisionPreselectionChange,
    handleContingencyPreselectionChange,
    handleFinalizeContingencyDecision,
    handleFinalizeSubitemDecision,
    canForceFinalizeSubitemDecision,
    canFinalizeContingencyDecision,
    loading,
  } = useDecisionItemMutations<QuotePreview_ItemFragment>(
    quote.projectId,
    quote.id,
    true,
    expandedItemIds,
    refetchWizardQuestions
  );

  const collapseItemAndChildrenRecursively = React.useCallback(
    (item: QuotePreview_ItemFragment) => {
      if (!expandedStore) return;
      const toCollapseIds = allDocItems
        .filter(i => i.pathForPdf.startsWith(item.pathForPdf))
        .map(i => i.id);
      const toExpandItemIds = expandedStore.areAllItemsExpanded
        ? allDocItems
            .filter(i => i.hasChildren && !toCollapseIds.includes(i.id))
            .map(i => i.id)
        : [];
      expandedStore.setExpandedState(
        Object.fromEntries([
          [ALL_KEY, false],
          ...toExpandItemIds.map(id => [id, true]),
          ...toCollapseIds.map(id => [id, false]),
        ])
      );
    },
    [expandedStore, allDocItems]
  );

  const additionalColumns = React.useMemo(
    (): AdditionalColumn<QuotePreview_ItemFragment>[] | undefined =>
      hasS4HanaData
        ? [
            {
              columns: 2,
              label: t("Ref no.", { ns: "Quote" }),
              render: item => {
                const data = sapS4HanaData.find(
                  data => data.itemId === item.id
                );
                return <>{data?.xId ?? null}</>;
              },
            },
          ]
        : undefined,
    [hasS4HanaData, sapS4HanaData, t]
  );

  const latest = useLatest({ refetchAfterApplyItemAction });

  const refetchAfterAnyFinalize = React.useCallback(async () => {
    await latest.current.refetchAfterApplyItemAction?.();
    await refetchWizardQuestions();
  }, [latest, refetchWizardQuestions]);

  const fields = React.useMemo(
    (): Fields<QuotePreview_ItemFragment> => ({
      CollapseButton: {
        render: item => {
          if (!expandedStore) return null;
          const expanded = expandedStore.isItemExpanded(item.id);
          return item.hasChildren ? (
            <CollapseButton
              aria-label={expanded ? "Collapse" : "Expand"}
              isExpanded={expanded}
              setIsExpanded={newExpanded => {
                if (newExpanded) {
                  expandedStore.setItemExpanded(item.id, newExpanded);
                } else {
                  collapseItemAndChildrenRecursively(item);
                }
              }}
            />
          ) : null;
        },
      },
      NonBindingCaption: {
        render: item => {
          if (item.type === "paid" && item.binding === "non_binding") {
            return t("Non-Binding item", {
              ns: "Quote",
            });
          }

          return null;
        },
      },
      ContingencyOptionalCaption: {
        render: item => {
          if (item.decisionIsContingentItem) {
            const captions: string[] = [];
            captions.push(
              t("Optional item", {
                ns: "Quote",
              })
            );

            if (item.decisionContingentItemPreselection) {
              captions.push(
                t("Recommended", {
                  ns: "Quote",
                })
              );
            }

            return captions.join(" • ");
          }

          return null;
        },
      },
      ContingencyOptionsButtons: {
        render: item => {
          return item.decisionIsContingentItem ? (
            <RestrictedByDocumentPermissionWithDebug
              permission="quote_decisions_decide"
              document={quote}
              otherwise={
                <ContingencyCheckbox
                  item={item}
                  canEdit={false}
                  disabled={true}
                  handleContingencyPreselectionChange={noop}
                />
              }
            >
              <ContingencyCheckbox
                item={item}
                loading={loading}
                canEdit={true}
                handleContingencyPreselectionChange={async (...args) => {
                  await handleContingencyPreselectionChange(...args);
                  await refetch?.();
                }}
              />
            </RestrictedByDocumentPermissionWithDebug>
          ) : null;
        },
      },
      ContingencyFinalizeButton: {
        render: item => {
          return item.decisionIsContingentItem ? (
            <RestrictedByDocumentPermissionWithDebug
              permission="quote_decisions_decide"
              document={quote}
            >
              <Button
                disabled={loading || !canFinalizeContingencyDecision(item)}
                size="extra-small"
                variant="contained"
                color="primary"
                onClick={async e => {
                  e.preventDefault();
                  e.stopPropagation();
                  await handleFinalizeContingencyDecision(item.id);
                  await refetch?.();
                  await refetchAfterAnyFinalize();
                }}
              >
                {item.decisionContingentItemPreselection === true
                  ? t("Keep item", {
                      ns: "Quote",
                    })
                  : t("Remove item", { ns: "Quote" })}
              </Button>
            </RestrictedByDocumentPermissionWithDebug>
          ) : null;
        },
      },
      DecisionOptionButton: {
        render: (item, parent) => {
          return parent?.type === "decision" &&
            !item.decisionOptionElimination ? (
            <RestrictedByDocumentPermissionWithDebug
              permission="quote_decisions_decide"
              document={quote}
              otherwise={
                <DecisionOptionRadioOrCheckbox
                  docId={quote.id}
                  projectId={quote.projectId}
                  item={item}
                  disabled={true}
                  expandedItemIds={expandedItemIds}
                />
              }
            >
              <DecisionOptionRadioOrCheckbox
                docId={quote.id}
                projectId={quote.projectId}
                item={item}
                disabled={loading}
                expandedItemIds={expandedItemIds}
                refetch={refetch}
              />
            </RestrictedByDocumentPermissionWithDebug>
          ) : null;
        },
      },
      DecisionNoneOptionButton: {
        render: item => {
          return item.decisionBehaviorOfSubitems === "SELECT_ONE" ? (
            <RestrictedByDocumentPermissionWithDebug
              permission="quote_decisions_decide"
              document={quote}
              otherwise={
                <SubitemsOptionsNoneSection
                  item={item}
                  handleDecisionPreselectionChange={noop}
                  canEdit={false}
                  disabled={true}
                />
              }
            >
              <SubitemsOptionsNoneSection
                item={item}
                loading={loading}
                handleDecisionPreselectionChange={async (...args) => {
                  await handleDecisionPreselectionChange(...args);
                  await refetch?.();
                }}
                canEdit={true}
              />
            </RestrictedByDocumentPermissionWithDebug>
          ) : null;
        },
      },
      DecisionButton: {
        render: item => {
          const showRefinementWizardButton = hasItemAnyQuestions(item.id);

          return showRefinementWizardButton ? (
            <RefinementWizardButton
              projectId={projectId}
              quote={quote}
              item={item}
              askWhen="onQuoteRefinement"
              refetchAfterApplyItemAction={refetchAfterAnyFinalize}
            />
          ) : null;
        },
      },
      DecisionFinalizeButton: {
        render: item => {
          return item.type === "decision" ? (
            <RestrictedByDocumentPermissionWithDebug
              permission="quote_decisions_decide"
              document={quote}
            >
              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  flexWrap: "wrap",
                }}
              >
                <Button
                  disabled={loading || !canForceFinalizeSubitemDecision(item)}
                  size="extra-small"
                  variant="contained"
                  color="primary"
                  startIcon={<CheckIcon />}
                  onClick={async e => {
                    e.preventDefault();
                    e.stopPropagation();

                    if (item.decisionIsContingentItem) {
                      if (item.decisionContingentItemPreselection !== true) {
                        await handleContingencyPreselectionChange(
                          item.id,
                          true
                        );
                      }
                      await handleFinalizeContingencyDecision(item.id);
                    }

                    await handleFinalizeSubitemDecision(item.id);
                    await refetch?.();
                    await refetchAfterAnyFinalize();
                  }}
                >
                  {t("Finalize decision", {
                    ns: "Quote",
                  })}
                </Button>
              </Stack>
            </RestrictedByDocumentPermissionWithDebug>
          ) : null;
        },
      },
      Pos: {
        render: item => <TextWithOverflow>{item.pathForPdf}</TextWithOverflow>,
      },
      Title: {
        render: item => {
          if (item.isRootItem) return null;
          if (!editable) {
            return (
              <PreviewValue
                sx={{ wordBreak: "break-word" }}
                value={item.title}
                changedValue={item.pendingChangeAttributes?.title}
              />
            );
          }
          const pendingTitle = item.pendingChangeAttributes?.title;
          const titleEditor = (
            <NoPropagateBox preventDefault={false}>
              <EditableTypography
                variant="inherit"
                value={pendingTitle ?? item.title}
                allowEmpty={false}
                sx={{ wordBreak: "break-word", mr: 1 }}
                placeholder={t("Item title", { ns: "Quote" })}
                onChange={async value => {
                  await modifyItemInfo(item.id, { title: value });
                  await refetch?.();
                }}
              />
            </NoPropagateBox>
          );
          // No outer Typography wrapper — the item title inherits typography
          // from the surrounding column / row context (matching the read-only
          // `<PreviewValue>` branch above). `EditableTypography variant="inherit"`
          // picks up the inherited styling.
          return pendingTitle !== undefined && pendingTitle !== item.title ? (
            <PendingChangeValue
              sx={{ wordBreak: "break-word" }}
              value={item.title}
              changedValue={titleEditor}
            />
          ) : (
            titleEditor
          );
        },
      },
      Description: {
        render: item => {
          if (!quoteItemHasDescription(item, quote.productsVisibility))
            return null;
          if (item.isRootItem) return null;
          return (
            <QuotePreviewItemDescription
              item={item}
              productsVisibility={quote.productsVisibility}
              columns={3}
              editing={
                editable
                  ? {
                      onChangeDescription: async value => {
                        await modifyItemInfo(item.id, { description: value });
                        await refetch?.();
                      },
                      onChangeProductTitle: async value => {
                        await modifyItemProduct(item.id, {
                          productTitle: value,
                        });
                        await refetch?.();
                      },
                      onChangeProductDescription: async value => {
                        await modifyItemProduct(item.id, {
                          productDescription: value,
                        });
                        await refetch?.();
                      },
                    }
                  : undefined
              }
            />
          );
        },
      },
      Images: {
        render: item => {
          const images = filterItemProductImages(
            item.attachments
              .filter(attachment => attachment.clientVisibility)
              .map(processAttachment)
              .filter(isImageOr3dModel),
            quote.productsVisibility
          );
          return images.length > 0 ? (
            <GalleryRow
              images={images}
              maxItems={4}
              style={
                isMinTablet && item.level > 2
                  ? { maxWidth: "300px" }
                  : undefined
              }
              objectFit="contain"
            />
          ) : null;
        },
      },
      Attachments: {
        render: item => {
          const files = item.attachments
            .filter(attachment => attachment.clientVisibility)
            .map(processAttachment)
            .filter(isPdfOrFile);
          return files.length > 0 ? (
            <CollapseSection
              title={t("Attachments", { ns: "Quote" })}
              titleVariant="body2"
              titleColor="secondary"
              iconButtonColor="secondary"
              sx={{ minHeight: "auto" }}
              isInitiallyExpanded={false}
              collapseTimeout={0}
            >
              <List
                disablePadding
                dense
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                }}
              >
                {files.map(attachment => (
                  <FileListItemButton
                    key={attachment.id}
                    file={attachment}
                    sx={{ minWidth: "200px", maxWidth: "320px", width: "auto" }}
                  />
                ))}
              </List>
            </CollapseSection>
          ) : null;
        },
      },
      Quantity: {
        render: (item, _, priceData) => {
          if (editable && item.proposedCalculation) {
            return (
              <EditableQuantityCell
                projectId={projectId}
                docId={quote.id}
                itemId={item.id}
                itemType={item.type}
                decisionBehaviorOfSubitems={item.decisionBehaviorOfSubitems}
                quantity={item.proposedCalculation.quantity}
                quantityUnit={item.proposedCalculation.quantityUnit}
                timePerUnit={item.proposedCalculation.timePerUnit}
                quantityExpression={
                  item.attributeExpressions.find(
                    e => e.attribute === "estimatedQuantity"
                  )?.expr
                }
                isTimeAndMaterialContract={isTimeAndMaterialContract}
                onCommitQuantity={async value => {
                  await modifyItemCalculation(item.id, {
                    estimatedQuantity: value,
                  });
                  await refetch?.();
                }}
                onCommitUnit={async value => {
                  await modifyItemCalculation(item.id, {
                    quantityUnit: value,
                  });
                  await refetch?.();
                }}
                onCommitTime={async value => {
                  await modifyItemCalculation(item.id, {
                    estimatedTime: value,
                  });
                  await refetch?.();
                }}
                onExpressionChange={async () => {
                  await refetch?.();
                }}
              />
            );
          }
          return priceData.rows &&
            priceData.rows.length > 0 &&
            priceData.rows.some(
              row =>
                (row.quantity?.agreed ?? row.quantity?.proposed) !== undefined
            ) ? (
            <Stack direction={"column"}>
              {priceData.rows.map((row, index) => (
                <PreviewValue
                  key={index}
                  value={row.quantity?.agreed ?? row.quantity?.proposed}
                  changedValue={row.quantity?.proposed}
                />
              ))}
            </Stack>
          ) : null;
        },
      },
      UnitPrice: {
        render: (item, _, priceData) => {
          return priceData.rows &&
            priceData.rows.length > 0 &&
            priceData.rows.some(
              row =>
                (row.unitPrice?.agreed ?? row.unitPrice?.proposed) !== undefined
            ) ? (
            <RestrictedByDocumentPermissionWithDebug
              permission="quote_document_read"
              document={quote}
            >
              <Stack direction={"column"}>
                {priceData.rows.map((row, index) => (
                  <PreviewValue
                    key={index}
                    value={row.unitPrice?.agreed ?? row.unitPrice?.proposed}
                    changedValue={row.unitPrice?.proposed}
                  />
                ))}
              </Stack>
            </RestrictedByDocumentPermissionWithDebug>
          ) : null;
        },
      },
      Subtotal: {
        render: (item, _, priceData) => {
          return priceData.rows &&
            priceData.rows.length > 0 &&
            priceData.rows.some(
              row =>
                (row.subtotal?.agreed ?? row.subtotal?.proposed) !== undefined
            ) ? (
            <RestrictedByDocumentPermissionWithDebug
              permission="quote_document_read"
              document={quote}
            >
              <Stack direction={"column"}>
                {priceData.rows.map((row, index) => (
                  <PreviewValue
                    key={index}
                    value={row.subtotal?.agreed ?? row.subtotal?.proposed}
                    changedValue={row.subtotal?.proposed}
                  />
                ))}
              </Stack>
            </RestrictedByDocumentPermissionWithDebug>
          ) : null;
        },
      },
      TotalPrice: {
        render: (item, _, priceData) => {
          return (
            <RestrictedByDocumentPermissionWithDebug
              permission="quote_document_read"
              document={quote}
            >
              <PreviewValue
                value={priceData.total?.agreed ?? priceData.total?.proposed}
                changedValue={priceData.total?.proposed}
              />
            </RestrictedByDocumentPermissionWithDebug>
          );
        },
      },
    }),
    [
      expandedStore,
      collapseItemAndChildrenRecursively,
      t,
      quote,
      loading,
      handleContingencyPreselectionChange,
      refetch,
      canFinalizeContingencyDecision,
      handleFinalizeContingencyDecision,
      refetchAfterAnyFinalize,
      expandedItemIds,
      handleDecisionPreselectionChange,
      hasItemAnyQuestions,
      projectId,
      canForceFinalizeSubitemDecision,
      handleFinalizeSubitemDecision,
      isMinTablet,
    ]
  );

  const extraActors = React.useMemo(
    () =>
      quote.actors.filter(
        actor =>
          !["CONTRACTOR", "CONTRACTEE"].includes(actor.type) &&
          (quote.headerVisibility.isEndClientVisible || actor.type !== "CLIENT")
      ),
    [quote.actors, quote.headerVisibility.isEndClientVisible]
  );

  return (
    <Stack
      direction={"column"}
      spacing={1}
      sx={{
        width: "100%",
      }}
    >
      <QuotePreviewHeader
        quote={doc}
        canEdit={isViewersDocument && !isAgreed}
      />
      {extraActors.length > 0 && (
        <Paper sx={{ position: "relative", padding: 2, paddingRight: 2 }}>
          <Stack
            direction="row"
            spacing={4}
            sx={{
              alignItems: "flex-start",
              justifyItems: "space-between",
            }}
          >
            <Stack
              direction="row"
              spacing={4}
              sx={{
                flex: 1,
              }}
            >
              {extraActors.map(actor => (
                <DocumentActorData actorType={actor.type} actor={actor} />
              ))}
            </Stack>
            <RestrictedByDocumentPermissionWithDebug
              permission="quote_actors_update"
              document={quote}
            >
              <ModalOpenButton
                Modal={QuoteDocumentActorsModal}
                modalProps={{ projectId: quote.projectId, quoteId: quote.id }}
              >
                <IconButton color="secondary" size="small">
                  <EditIcon />
                </IconButton>
              </ModalOpenButton>
            </RestrictedByDocumentPermissionWithDebug>
          </Stack>
        </Paper>
      )}
      {hasSapS4HanaIntegration && (
        <S4HanaQuoteDataBox
          quote={quote}
          projectId={quote.projectId}
          quoteId={quote.id}
          canEdit={isViewersDocument && !isAgreed}
        />
      )}
      <Paper>
        <Box
          sx={{
            padding: 2,
          }}
        >
          <div
            style={{
              float: "right",
              display: "flex",
              position: "relative",
              zIndex: 1,
            }}
          >
            {rootItem &&
              (hasItemAnyQuestions(rootItem.id) ? (
                <RefinementWizardButton
                  projectId={projectId}
                  quote={quote}
                  item={rootItem}
                  askWhen="onQuoteRefinement"
                  refetchAfterApplyItemAction={refetchWizardQuestions}
                />
              ) : null)}
          </div>
          <Stack direction={"column"} spacing={1}>
            {editable && rootItem ? (
              <EditableTypography
                variant="h2"
                value={doc.title}
                allowEmpty={false}
                placeholder={t("Quote title", { ns: "Quote" })}
                onChange={async value => {
                  await modifyItemInfo(rootItem.id, { title: value });
                  await refetch?.();
                }}
              />
            ) : (
              <Typography variant="h2">{doc.title}</Typography>
            )}
            {doc.isBinding === false && (
              <Typography
                color="textSecondary"
                component="div"
                sx={{
                  fontWeight: "bold",
                }}
              >
                {t("Non-Binding Quote", {
                  ns: "QuoteStateBadge",
                })}
              </Typography>
            )}
          </Stack>
        </Box>
      </Paper>
      <OpeningTextInput
        canEdit={!doc.isPublished}
        content={doc.quoteTexts?.opening}
        readViewContent={doc.interpolatedQuoteTexts?.opening}
        onSave={saveQuoteText}
        showInfo={true}
        renderEditIconButton={button => (
          <RestrictedByDocumentPermissionWithDebug
            permission="quote_settings_update"
            document={quote}
          >
            {button}
          </RestrictedByDocumentPermissionWithDebug>
        )}
      />
      {rootItem &&
        (images.length > 0 ||
          quoteItemHasDescription(rootItem, quote.productsVisibility)) && (
          <Paper>
            <Stack
              direction={"column"}
              spacing={2}
              sx={{
                padding: 2,
              }}
            >
              <GalleryRow
                images={images}
                maxItems={isMinDesktop ? 8 : isMinTablet ? 6 : 4}
              />

              <QuotePreviewItemDescription
                item={rootItem}
                productsVisibility={quote.productsVisibility}
                editing={
                  editable
                    ? {
                        onChangeDescription: async value => {
                          await modifyItemInfo(rootItem.id, {
                            description: value,
                          });
                          await refetch?.();
                        },
                        onChangeProductTitle: async value => {
                          await modifyItemProduct(rootItem.id, {
                            productTitle: value,
                          });
                          await refetch?.();
                        },
                        onChangeProductDescription: async value => {
                          await modifyItemProduct(rootItem.id, {
                            productDescription: value,
                          });
                          await refetch?.();
                        },
                      }
                    : undefined
                }
              />
            </Stack>
          </Paper>
        )}
      <DocumentPreviewItems<QuotePreview_ItemFragment>
        doc={doc}
        fields={fields}
        additionalColumns={additionalColumns}
        allDocItems={allDocItems}
        navigateToItem={navigateToItem}
        selectedItemId={selectedItemId}
        isTimeAndMaterialContract={isTimeAndMaterialContract}
        getItemStyle={getQuoteItemStyle}
        shouldRenderItem={shouldRenderQuoteItem}
        showCollapseButton={Boolean(expandedStore)}
        container={container}
        expandedStore={expandedStore}
      />
      {doc.proposedCalculation && (
        <QuotePreviewSummary
          vatVisibility={doc.vatVisibility}
          discountPercentage={doc.discountPercentage}
          discountReason={doc.discountReason}
          isDiscountPercentageVisible={doc.isDiscountPercentageVisible}
          calculation={
            doc.agreement === "YES"
              ? doc.agreedCalculation
              : doc.proposedCalculation
          }
          changedCalculation={doc.proposedCalculation}
        />
      )}
      {quote.showOfferConditions && (
        <QuotePreviewDates
          quote={quote}
          projectId={quote.projectId}
          quoteId={quote.id}
          earliestDate={doc.earliestDate}
          duration={doc.duration}
          expirationDate={doc.expirationDate}
          acceptedAt={doc.acceptedAt}
          isPublished={doc.isPublished}
          canEdit={isViewersDocument && !isAgreed}
        />
      )}
      <ClosingTextInput
        canEdit={isViewersDocument && !isAgreed}
        content={doc.quoteTexts?.closing}
        readViewContent={doc.interpolatedQuoteTexts?.closing}
        onSave={saveQuoteText}
        showInfo={true}
        renderEditIconButton={button => (
          <RestrictedByDocumentPermissionWithDebug
            permission="quote_settings_update"
            document={quote}
          >
            {button}
          </RestrictedByDocumentPermissionWithDebug>
        )}
      />
      {doc.isPublished && (
        <Paper>
          <Box
            sx={{
              p: 2,
            }}
          >
            <LabeledSignatureValue
              canEdit={false}
              label={t("Signature", { ns: "Quote" })}
              value={
                doc.agreement === "YES" && !doc.needsAgreement
                  ? (doc.agreementSignature ?? null)
                  : null
              }
            />
          </Box>
        </Paper>
      )}
      {files.length > 0 && (
        <FilesBoxTable
          title={t("Attachments", { ns: "Quote" })}
          attachments={files}
          canEdit={false}
          gridColumns={isMinDesktop ? 8 : isMinTablet ? 6 : 4}
        />
      )}
    </Stack>
  );
};

const getQuoteItemStyle = (
  theme: Theme,
  item: QuotePreview_ItemFragment
): React.CSSProperties | undefined => ({
  color:
    item.decisionOptionElimination ||
    item.isParentDecisionNotPreselected ||
    item.isParentContingencyNotPreselected ||
    (item.decisionIsContingentItem &&
      item.decisionContingentItemPreselection !== true)
      ? theme.palette.grey[600]
      : item.type === "paid" && item.binding === "non_binding"
        ? theme.palette.secondary.main
        : undefined,
  fontStyle:
    item.type === "paid" && item.binding === "non_binding"
      ? "italic"
      : undefined,
  textDecoration:
    item.deletedAt || item.pendingChangeAttributes.deletedAt
      ? "line-through"
      : undefined,
  backgroundColor: item.pendingChangeAttributes.deletedAt
    ? color.yellowLight
    : undefined,
});

function shouldRenderQuoteItem(item: QuotePreview_ItemFragment) {
  return !item.isItemEliminated && !item.isAnyParentItemEliminated;
}
